<template>
  <div class="brand-news">
    <div class="news-header">
      <h2>Latest News & Updates</h2>
      <p>Stay informed about our latest developments and insights</p>
    </div>

    <div id="news-pulses-container" class="news-container">
      <div class="loading-news">
        <div class="loading-spinner"></div>
        <p>Loading latest updates...</p>
      </div>
    </div>
  </div>
</template>

<script>
import { postRequest } from '@/apis/front'

export default {
  name: 'BrandNews',
  props: {
    userId: {
      type: String,
      default: '68b11e1d94c784e0568b4590'
    },
    maxItems: {
      type: Number,
      default: null
    }
  },
  data() {
    return {
      pulses: [],
      loading: true,
      error: null
    }
  },
  mounted() {
    this.loadNews()
  },
  methods: {
    async loadNews() {
      this.loading = true
      this.error = null

      try {
        const response = await postRequest('getUserPulses', {
          user_id: this.userId
        })

        if (response.data && response.data.code === 0) {
          this.pulses = this.extractPulses(response.data)
          this.renderPulses()
        } else {
          throw new Error(response.data?.msg || 'Failed to load news')
        }
      } catch (error) {
        console.error('Error loading news:', error)
        this.error = error
        this.showError(error)
      } finally {
        this.loading = false
      }
    },

    extractPulses(data) {
      if (Array.isArray(data)) {
        return data
      } else if (data.data && Array.isArray(data.data)) {
        return data.data
      } else if (data.pulses && Array.isArray(data.pulses)) {
        return data.pulses
      } else if (data.result && Array.isArray(data.result)) {
        return data.result
      }
      return []
    },

    formatDate(timestamp) {
      if (!timestamp) return 'Unknown date'
      const date = new Date(timestamp * 1000)
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      })
    },

    getPulseDisplayDate(pulse) {
      if (pulse.display_date) {
        if (typeof pulse.display_date === 'string') {
          return pulse.display_date
        }
        if (typeof pulse.display_date === 'number') {
          return this.formatDate(pulse.display_date)
        }
      }

      if (pulse.created_at) {
        return this.formatDate(pulse.created_at)
      }

      return 'Unknown date'
    },

    getSortTimestamp(pulse) {
      if (pulse.display_date && typeof pulse.display_date === 'number') {
        return pulse.display_date
      }
      if (pulse.updated_at) {
        return pulse.updated_at
      }
      return pulse.created_at || 0
    },

    renderPulse(pulse) {
      const displayDate = this.getPulseDisplayDate(pulse)
      const hasDisplayDate = pulse.display_date
      const firstImage = pulse.images && pulse.images.length > 0 ? pulse.images[0] : null
      const hasMultipleImages = pulse.images && pulse.images.length > 1

      let tagsHtml = ''
      if (pulse.tags && pulse.tags.length > 0) {
        tagsHtml = '<div class="pulse-tags">' +
          pulse.tags.map(tag => `<span class="pulse-tag">${tag}</span>`).join('') +
          '</div>'
      }

      // Render markdown content
      let htmlContent = pulse.content || ''

      // Simple markdown processing (you can enhance this with a markdown library)
      htmlContent = this.parseMarkdown(htmlContent)

      const pulseData = JSON.stringify(pulse)
        .replace(/&/g, '&amp;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')

      return `
        <article class="news-article dynamic-post" data-pulse="${pulseData}">
          <div class="news-content-wrapper">
            <div class="news-meta">
              <span class="news-date">${displayDate}</span>
              ${!hasDisplayDate && pulse.updated_at && pulse.updated_at !== pulse.created_at ?
                `<span class="news-updated">Updated: ${this.formatDate(pulse.updated_at)}</span>` : ''}
            </div>
            <div class="markdown-content">
              ${htmlContent}
            </div>
            ${tagsHtml}
          </div>
          ${firstImage ? `
            <div class="news-image-wrapper">
              <img src="${firstImage}" alt="News image" loading="lazy" />
              ${hasMultipleImages ? `<span class="image-count-badge">+${pulse.images.length - 1} more</span>` : ''}
            </div>
          ` : ''}
        </article>
      `
    },

    parseMarkdown(text) {
      if (!text) return ''

      // Simple markdown processing
      let html = text

      // Headers
      html = html.replace(/^### (.*$)/gim, '<h3>$1</h3>')
      html = html.replace(/^## (.*$)/gim, '<h2>$1</h2>')
      html = html.replace(/^# (.*$)/gim, '<h1>$1</h1>')

      // Bold
      html = html.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')

      // Italic
      html = html.replace(/\*(.+?)\*/g, '<em>$1</em>')

      // Links
      html = html.replace(/\[(.+?)\]\((.+?)\)/g, '<a href="$2" target="_blank">$1</a>')

      // Line breaks
      html = html.replace(/\n/g, '<br>')

      return html
    },

    renderPulses() {
      const container = document.getElementById('news-pulses-container')
      if (!container) return

      if (!this.pulses || this.pulses.length === 0) {
        container.innerHTML = `
          <div class="error-news">
            <p>No news updates available at this time.</p>
          </div>
        `
        return
      }

      // Sort by timestamp
      const sortedPulses = [...this.pulses].sort((a, b) => {
        return this.getSortTimestamp(b) - this.getSortTimestamp(a)
      })

      const pulsesToRender = this.maxItems
        ? sortedPulses.slice(0, this.maxItems)
        : sortedPulses

      container.innerHTML = pulsesToRender.map(pulse => this.renderPulse(pulse)).join('')

      // Attach click handlers
      this.attachModalHandlers()
    },

    attachModalHandlers() {
      const articles = document.querySelectorAll('#news-pulses-container .news-article.dynamic-post')
      articles.forEach(article => {
        article.addEventListener('click', (e) => {
          if (e.target.tagName === 'A' || e.target.tagName === 'BUTTON') {
            return
          }

          const pulseData = article.getAttribute('data-pulse')
          if (pulseData) {
            try {
              const decodedData = pulseData
                .replace(/&quot;/g, '"')
                .replace(/&#39;/g, "'")
                .replace(/&amp;/g, '&')
                .replace(/&lt;/g, '<')
                .replace(/&gt;/g, '>')
              const pulse = JSON.parse(decodedData)
              this.openModal(pulse)
            } catch (error) {
              console.error('Error parsing pulse data:', error)
            }
          }
        })
      })
    },

    openModal(pulse) {
      let modal = document.getElementById('news-pulse-modal')
      if (!modal) {
        modal = document.createElement('div')
        modal.id = 'news-pulse-modal'
        modal.className = 'news-modal'
        document.body.appendChild(modal)
      }

      let htmlContent = this.parseMarkdown(pulse.content || '')

      let imagesHtml = ''
      if (pulse.images && pulse.images.length > 0) {
        imagesHtml = '<div class="news-modal-images">' +
          pulse.images.map(img =>
            `<img src="${img}" alt="News image" loading="lazy" />`
          ).join('') +
          '</div>'
      }

      let tagsHtml = ''
      if (pulse.tags && pulse.tags.length > 0) {
        tagsHtml = '<div class="pulse-tags">' +
          pulse.tags.map(tag => `<span class="pulse-tag">${tag}</span>`).join('') +
          '</div>'
      }

      const displayDate = this.getPulseDisplayDate(pulse)
      const hasDisplayDate = pulse.display_date

      modal.innerHTML = `
        <div class="news-modal-content">
          <div class="news-modal-header">
            <div>
              <span class="news-date">${displayDate}</span>
              ${!hasDisplayDate && pulse.updated_at && pulse.updated_at !== pulse.created_at ?
                `<span class="news-updated">Updated: ${this.formatDate(pulse.updated_at)}</span>` : ''}
            </div>
            <button class="news-modal-close" aria-label="Close">&times;</button>
          </div>
          <div class="news-modal-body">
            <div class="markdown-content">
              ${htmlContent}
            </div>
            ${tagsHtml}
            ${imagesHtml}
          </div>
        </div>
      `

      modal.classList.add('active')

      const closeBtn = modal.querySelector('.news-modal-close')
      closeBtn.addEventListener('click', () => this.closeModal())

      modal.addEventListener('click', (e) => {
        if (e.target === modal) {
          this.closeModal()
        }
      })

      const escapeHandler = (e) => {
        if (e.key === 'Escape') {
          this.closeModal()
          document.removeEventListener('keydown', escapeHandler)
        }
      }
      document.addEventListener('keydown', escapeHandler)
    },

    closeModal() {
      const modal = document.getElementById('news-pulse-modal')
      if (modal) {
        modal.classList.remove('active')
        setTimeout(() => {
          if (modal.parentNode) {
            modal.parentNode.removeChild(modal)
          }
        }, 300)
      }
    },

    showError(error) {
      const container = document.getElementById('news-pulses-container')
      if (container) {
        container.innerHTML = `
          <div class="error-news">
            <p>⚠️ Unable to load latest updates</p>
            <p class="error-details">${error.message || 'An error occurred'}</p>
            <button @click="loadNews" class="btn btn-primary btn-small">Retry</button>
          </div>
        `
      }
    }
  }
}
</script>

<style scoped>
.brand-news {
  padding: 80px 0;
  background: linear-gradient(135deg, #f5f7fa 0%, #e8ecf1 100%);
  min-height: 100vh;
}

.news-header {
  text-align: center;
  margin-bottom: 60px;
  padding: 0 20px;
}

.news-header h2 {
  font-size: 40px;
  color: #303133;
  margin-bottom: 12px;
  font-weight: 700;
}

.news-header p {
  font-size: 18px;
  color: #606266;
}

.news-container {
  display: flex;
  flex-direction: column;
  gap: 0;
  max-width: 900px;
  margin: 0 auto;
  padding: 0 20px;
}

/* Loading State */
.loading-news {
  text-align: center;
  padding: 80px 40px;
  color: #606266;
  background: white;
  border-radius: 12px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.06);
}

.loading-spinner {
  width: 48px;
  height: 48px;
  border: 5px solid #e4e7ed;
  border-top-color: #409EFF;
  border-radius: 50%;
  margin: 0 auto 20px;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

.loading-news p {
  font-size: 15px;
  font-weight: 500;
}

/* Error State */
.error-news {
  text-align: center;
  padding: 80px 40px;
  color: #909399;
  background: white;
  border-radius: 12px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.06);
}

.error-news p {
  font-size: 16px;
  margin-bottom: 12px;
}

.error-details {
  font-size: 14px;
  color: #c0c4cc;
  margin-top: 8px;
  margin-bottom: 24px;
}

/* News Article - Using :deep() for dynamic content */
:deep(.news-article) {
  background: white;
  border-radius: 16px;
  padding: 40px;
  box-shadow: 0 6px 24px rgba(0, 0, 0, 0.08);
  border: 2px solid #e8ecf1;
  display: flex;
  gap: 32px;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  overflow: hidden;
  margin-bottom: 24px;
}

:deep(.news-article::before) {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 5px;
  background: linear-gradient(90deg, #409EFF 0%, #67C23A 100%);
  opacity: 1;
  transition: all 0.3s ease;
}

:deep(.news-article:hover::before) {
  opacity: 1;
}

:deep(.news-article:hover) {
  box-shadow: 0 12px 36px rgba(0, 0, 0, 0.15);
  transform: translateY(-6px);
  border-color: #409EFF;
}

:deep(.news-article:hover::before) {
  height: 6px;
}

:deep(.news-content-wrapper) {
  flex: 1;
}

:deep(.news-meta) {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 16px;
  padding-bottom: 12px;
  border-bottom: 1px solid #f0f0f0;
}

:deep(.news-date) {
  font-size: 13px;
  color: #606266;
  font-weight: 600;
  letter-spacing: 0.3px;
  background: linear-gradient(135deg, #f5f7fa 0%, #e8ecf1 100%);
  padding: 4px 12px;
  border-radius: 6px;
  display: inline-block;
}

:deep(.news-updated) {
  font-size: 12px;
  color: #909399;
  font-style: italic;
}

:deep(.markdown-content) {
  color: #606266;
  line-height: 1.7;
  font-size: 15px;
}

:deep(.markdown-content h1),
:deep(.markdown-content h2),
:deep(.markdown-content h3) {
  color: #303133;
  margin-bottom: 12px;
  margin-top: 16px;
  font-weight: 700;
  line-height: 1.4;
}

:deep(.markdown-content h1) {
  font-size: 26px;
}

:deep(.markdown-content h2) {
  font-size: 22px;
}

:deep(.markdown-content h3) {
  font-size: 19px;
}

:deep(.markdown-content p) {
  margin-bottom: 12px;
}

:deep(.markdown-content a) {
  color: #409EFF;
  text-decoration: none;
}

:deep(.markdown-content a:hover) {
  text-decoration: underline;
}

:deep(.pulse-tags) {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-top: 20px;
  padding-top: 16px;
  border-top: 1px solid #f0f0f0;
}

:deep(.pulse-tag) {
  display: inline-block;
  padding: 6px 14px;
  background: linear-gradient(135deg, #ecf5ff 0%, #e1f0ff 100%);
  color: #409EFF;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 600;
  transition: all 0.2s ease;
  border: 1px solid rgba(64, 158, 255, 0.2);
}

:deep(.pulse-tag:hover) {
  background: linear-gradient(135deg, #409EFF 0%, #66b1ff 100%);
  color: white;
  transform: translateY(-1px);
  box-shadow: 0 2px 8px rgba(64, 158, 255, 0.3);
}

:deep(.news-image-wrapper) {
  flex-shrink: 0;
  width: 240px;
  height: 180px;
  position: relative;
  border-radius: 10px;
  overflow: hidden;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);
}

:deep(.news-image-wrapper img) {
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.3s ease;
}

:deep(.news-article:hover .news-image-wrapper img) {
  transform: scale(1.05);
}

:deep(.image-count-badge) {
  position: absolute;
  bottom: 8px;
  right: 8px;
  background: rgba(0, 0, 0, 0.7);
  color: white;
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 500;
}

/* Modal Styles */
:deep(.news-modal) {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  opacity: 0;
  pointer-events: none;
  transition: opacity 0.3s ease;
}

:deep(.news-modal.active) {
  opacity: 1;
  pointer-events: all;
}

:deep(.news-modal-content) {
  background: white;
  border-radius: 8px;
  max-width: 800px;
  max-height: 90vh;
  width: 90%;
  overflow: auto;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.2);
}

:deep(.news-modal-header) {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 24px;
  border-bottom: 1px solid #e4e7ed;
  position: sticky;
  top: 0;
  background: white;
  z-index: 1;
}

:deep(.news-modal-close) {
  background: none;
  border: none;
  font-size: 32px;
  color: #909399;
  cursor: pointer;
  padding: 0;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: color 0.3s;
}

:deep(.news-modal-close:hover) {
  color: #303133;
}

:deep(.news-modal-body) {
  padding: 24px;
}

:deep(.news-modal-images) {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
  gap: 16px;
  margin-top: 24px;
}

:deep(.news-modal-images img) {
  width: 100%;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

/* Responsive */
@media (max-width: 768px) {
  .brand-news {
    padding: 60px 0;
  }

  .news-header {
    margin-bottom: 40px;
  }

  .news-header h2 {
    font-size: 32px;
  }

  .news-header p {
    font-size: 16px;
  }

  .news-container {
    gap: 24px;
    padding: 0 16px;
  }

  .news-article {
    flex-direction: column;
    padding: 24px;
  }

  .news-image-wrapper {
    width: 100%;
    height: 220px;
  }

  .markdown-content h1 {
    font-size: 22px;
  }

  .markdown-content h2 {
    font-size: 20px;
  }

  .markdown-content h3 {
    font-size: 18px;
  }

  :deep(.news-modal-content) {
    width: 95%;
    max-height: 95vh;
  }

  :deep(.news-modal-header),
  :deep(.news-modal-body) {
    padding: 16px;
  }
}

.btn {
  padding: 10px 20px;
  border-radius: 8px;
  border: none;
  cursor: pointer;
  font-size: 14px;
  font-weight: 600;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.btn-primary {
  background: linear-gradient(135deg, #409EFF 0%, #66b1ff 100%);
  color: white;
}

.btn-primary:hover {
  background: linear-gradient(135deg, #66b1ff 0%, #409EFF 100%);
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(64, 158, 255, 0.4);
}

.btn-small {
  padding: 8px 16px;
  font-size: 13px;
}
</style>
